<script setup lang="ts">
import { useSiteCriterias } from "@/api/vibration-criteria-query";
import { useSiteWeeklyLimits } from "@/api/weekly-limit-query";
import VibrationCriteriasSelect from "@/components/vibration-criteria/VibrationCriteriasSelect.vue";
import { Alert } from "@soft-db/monitoring-api-client";
import { Badge, Card, CardTitle, Progress } from "@soft-db/ui";
import { useConditionTypeLabel } from "../composable/condition-label";
import WeeklyLimitSelect from "../condition/WeeklyLimitSelect.vue";
import AlertMappingLimitDisplay from "./AlertMappingLimitDisplay.vue";
import AlertMappingRow from "./AlertMappingRow.vue";
import { ConditionMapping } from "./types";

const props = defineProps<{
	fromSite: string;
	toSite: string;
	alert: Alert;
}>();

const conditions = defineModel<ConditionMapping[]>({ required: true });

const { weeklyLimits, isLoading: weeklyLoading } = useSiteWeeklyLimits(() => props.fromSite);
const { vibrationCriterias, isLoading: vcLoading } = useSiteCriterias(() => props.fromSite);

const isLoading = logicOr(weeklyLoading, vcLoading);

function resolveLimitName(type: ConditionMapping["type"], id: string) {
	switch (type) {
		case "vibration_event":
			return vibrationCriterias.value.find((vc) => vc.id === id)?.name || "";
		case "weekly_limit":
			return weeklyLimits.value?.find((wl) => wl.id === id)?.name || "";
	}
}

const limitKeys = [
	{ severity: "error", from: "originalError", to: "newError" },
	{ severity: "warning", from: "originalWarning", to: "newWarning" },
] as const;

const conditionTypeLabel = useConditionTypeLabel();
</script>

<template>
	<Card>
		<Progress v-if="isLoading" />
		<CardTitle class="border-border flex items-center justify-between border-b p-3">
			<div>{{ alert.name }}</div>
			<Badge color="primary" variant="soft">{{ conditionTypeLabel(alert.type) }}</Badge>
		</CardTitle>

		<template v-if="conditions.length > 0">
			<div v-for="condition of conditions" :key="condition.index" class="flex flex-col gap-2 p-3">
				<template v-for="limit of limitKeys" :key="limit.severity">
					<AlertMappingRow v-if="condition[limit.from]">
						<template #left>
							<AlertMappingLimitDisplay
								:name="resolveLimitName(condition.type, condition[limit.from]!)"
								:severity="limit.severity"
							/>
						</template>
						<template #right>
							<VibrationCriteriasSelect
								v-if="condition.type === 'vibration_event'"
								v-model:criteria="condition[limit.to]"
								:site="toSite"
								label=""
							/>
							<WeeklyLimitSelect
								v-else
								v-model:weeklyLimit="condition[limit.to]"
								:site="toSite"
								label=""
							/>
						</template>
					</AlertMappingRow>
				</template>
			</div>
		</template>
	</Card>
</template>
